/**
 * Mana Locals Admin — inventory boundary schemas.
 *
 * CONTRACT SOURCE (API repository): M18 inventory,
 * `src/domains/m18-inventory/contracts/` — the stock balance and stock movement
 * projections served by `GET /api/v1/inventory/stock-balances` and
 * `GET /api/v1/inventory/stock-movements`.
 *
 * ---------------------------------------------------------------------------
 * QUANTITIES ARE STRINGS ON THE WIRE
 * ---------------------------------------------------------------------------
 * `onHand`, `reserved`, `available` and `quantity` arrive as DECIMAL STRINGS
 * ("250", "3"), not numbers. The API keeps them in the database as `numeric` so
 * a fractional quantity (2.5 kg) survives the round trip; JavaScript numbers
 * would silently lose that. They are therefore carried as strings here and
 * formatted at the edge, never parsed into a float.
 */
import { z } from 'zod';

import { CollectionResponseSchema, IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

/** A decimal quantity as the API sends it. Never coerced to a number. */
export const QuantitySchema = z.string().min(1);

export const StockBalanceSchema = z
  .object({
    id: UuidSchema,
    /** The offer this balance is for — M18 keys stock by the offer id. */
    itemId: z.string().min(1),
    locationId: z.string().min(1),
    onHand: QuantitySchema,
    reserved: QuantitySchema,
    available: QuantitySchema,
    unitOfMeasure: z.string().min(1),
    version: z.number().int().min(1),
    lastMovementId: UuidSchema.nullable(),
    updatedAt: IsoDateTimeSchema,
  })
  .passthrough();

export const StockMovementSchema = z
  .object({
    id: UuidSchema,
    movementType: z.string().min(1),
    itemId: z.string().min(1),
    locationId: z.string().min(1),
    targetLocationId: z.string().nullable(),
    quantity: QuantitySchema,
    unitOfMeasure: z.string().min(1),
    reason: z.string().nullable(),
    referenceId: z.string().nullable(),
    purchaseOrderId: z.string().nullable(),
    balanceVersion: z.number().int().min(1),
    createdAt: IsoDateTimeSchema,
  })
  .passthrough();

export const StockBalanceListResponseSchema = CollectionResponseSchema(StockBalanceSchema);
export const StockMovementListResponseSchema = CollectionResponseSchema(StockMovementSchema);

export type StockBalance = z.infer<typeof StockBalanceSchema>;
export type StockMovement = z.infer<typeof StockMovementSchema>;
